import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { HistorySection } from '@/features/audit-log';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { StatusBadge, type StatusVariant } from '@/shared/components/status-badge';
import { formatCurrency, formatDateTime } from '@/shared/lib/format';

import { useCouponRedemptions } from '../api/coupons.queries';
import { formatCouponScope, formatCouponValue } from '../lib/coupon-format';
import type { Coupon, CouponRedemptionStatus } from '../types/coupons.types';

const PAGE_SIZE = 10;

const STATUS: Record<CouponRedemptionStatus, { label: string; variant: StatusVariant }> = {
  Redeemed: { label: 'Used', variant: 'success' },
  Reserved: { label: 'Payment in progress', variant: 'info' },
  Released: { label: 'Released', variant: 'neutral' },
};

/** Who used a coupon, on which payment and orders, and for how much. */
export function CouponRedemptionsDialog({
  coupon,
  onClose,
}: {
  coupon: Coupon;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const redemptions = useCouponRedemptions(coupon.id, { page, pageSize: PAGE_SIZE });
  const data = redemptions.data;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            Uses of <span className="font-mono">{coupon.code}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {formatCouponValue(coupon, coupon.currency)} · {formatCouponScope(coupon)} ·{' '}
            {coupon.redeemedCount} used,{' '}
            {formatCurrency(coupon.redeemedDiscountTotal, coupon.currency ?? undefined, {
              precise: true,
            })}{' '}
            discounted in total.
          </DialogDescription>
        </DialogHeader>
        {redemptions.isLoading ? (
          <Skeleton className="h-32" />
        ) : redemptions.error || !data ? (
          <ErrorState error={redemptions.error} onRetry={() => void redemptions.refetch()} />
        ) : data.items.length === 0 ? (
          <EmptyState title="Not used yet" description="No checkout has used this coupon." />
        ) : (
          <>
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead className="text-end">Discount</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <Link
                        to={`/customers/${r.customerId}`}
                        className="text-primary hover:underline"
                      >
                        {r.customerName ?? `#${r.customerId}`}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={STATUS[r.status].variant}>
                        {STATUS[r.status].label}
                      </StatusBadge>
                      {r.releaseReason && (
                        <span className="text-muted-foreground block text-[11px]">
                          {r.releaseReason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">{r.billerTrxNo ?? '—'}</TableCell>
                    <TableCell>
                      {r.orderIds.length === 0
                        ? '—'
                        : r.orderIds.map((id, i) => (
                            <span key={id}>
                              {i > 0 && ', '}
                              <Link to={`/orders/${id}`} className="text-primary hover:underline">
                                #{id}
                              </Link>
                            </span>
                          ))}
                    </TableCell>
                    <TableCell className="text-end tabular-nums">
                      {formatCurrency(r.discountAmount, r.currency ?? undefined, { precise: true })}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDateTime(r.redeemedAt ?? r.releasedAt ?? r.reservedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.totalPages > 1 && (
              <div className="flex items-center justify-end gap-2 text-xs">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {data.totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= data.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
        <HistorySection
          entityType="Coupon"
          entityId={String(coupon.id)}
          title="Changes"
          collapsible
        />
      </DialogContent>
    </Dialog>
  );
}
